import { useRef, useState } from 'react';
import { Input, Button } from 'components/common';
import { getEnvironment, isValidEmailAddress, getCurrentLocaleString, getUrlLocale, locales } from 'components/utils';
import { renderParagraph } from 'components/utils/render';
import apiEndpoints from 'components/utils/apiEndpoints';
import {
    pushClickEvent,
    pushStartFormEvent,
    pushEnterFieldFormEvent,
    pushFormSubmitEvent,
    pushFormCompleteEvent,
    pushFormErrorFieldEvent,
} from '../../../tracking/events';
import { registerMixpanelSuperProperties } from '../../../tracking/mixpanel';
import { BlockContent, SignupSection } from '../AwarenessRaiserPage/AwarenessRaiserPage.models';
import {
    FormWrapper,
    NameRow,
    Field,
    FieldLabel,
    FieldError,
    ConsentRow,
    ConsentCheckbox,
    ConsentLabel,
    ButtonRow,
    ErrorText,
} from './PledgeForm.style';

export const PLEDGE_FORM_NAME = 'Awareness Raiser Pledge';

/**
 * Whether a pledge takes the form off the page, leaving Six Ways as the only
 * confirmation. This has flipped a few times between Cleo and Brandon, so it is
 * one switch rather than logic spread across the form and the pages.
 *
 * false - the form stays up and clears itself, so a second person can sign up
 *         on the same device, and attempt_number restarts for them.
 * true  - the form and the Read More sign up band disappear once pledged, and
 *         pledge_status can then only ever report 'new'.
 */
export const HIDE_FORM_AFTER_PLEDGE = false;

export const PLEDGE_FIELD_IDS = {
    firstName: 'pledgeForm--firstName',
    lastName: 'pledgeForm--lastName',
    email: 'pledgeForm--email',
    marketingConsent: 'pledgeForm--marketingConsent',
    termsPrivacy: 'pledgeForm--termsPrivacy',
};

export interface TextField {
    label: string;
    placeholder?: string;
    error?: string;
}

export interface ConsentItem {
    id: string;
    label: React.ReactNode;
    required: boolean;
    // Not sent to Salesforce. Both boxes must be ticked before submit.
    subscriptionKey?: string;
    // Show this checkbox above or below the submit button.
    position?: 'beforeButton' | 'afterButton';
}

export interface PledgeFormData {
    firstNameField: TextField;
    lastNameField: TextField;
    emailField: TextField;
    consents: ConsentItem[];
    btnText: string;
    subscriptions?: string;
    source?: string;
    formName?: string;
}

interface Props extends PledgeFormData {
    cms?: any;
    // Runs after a successful sign up so the Six Ways section can appear.
    onSuccess?: () => void;
    // Skip Salesforce and treat a valid form as success. Off unless passed in.
    mockSubmit?: boolean;
    // Browser already had the pledge flag when this page loaded.
    hadPledged?: boolean;
}

export const AWARENESS_PLEDGE_STORAGE_KEY = 'mo-awareness-landing-signed-up';

export const readAwarenessPledge = () => {
    try {
        return window.localStorage.getItem(AWARENESS_PLEDGE_STORAGE_KEY) === 'true';
    } catch (e) {
        return false;
    }
};

export const writeAwarenessPledge = () => {
    try {
        window.localStorage.setItem(AWARENESS_PLEDGE_STORAGE_KEY, 'true');
    } catch (e) {
        // Ignore: the reveal still works for this page view.
    }
};

const PLACEHOLDERS: { [field: string]: string } = {
    firstName: 'eg. Freddie',
    lastName: 'eg. Mercury',
    email: 'eg. freddie.mercury@gmail.com',
};

// Fallback only: CMS copy is empty until content is authored. Terms is served
// by a separate app that always namespaces by language, so it keeps the
// prefix even for a country's default language (/en/terms on AU).
const termsPath = () => {
    const { language } = getUrlLocale() as { language: string };
    return `/${language || 'en'}/terms`;
};

const localePath = (path: string) => {
    const { country, language } = getUrlLocale() as { country: string, language: string };
    const countryLocales = (locales as { [code: string]: { language: { code: string, default?: boolean }[] } })[country];
    const isDefault = countryLocales?.language?.some(entry => entry.code === language && entry.default);
    return language && !isDefault ? `/${language}${path}` : path;
};

const trackLegalLink = (linkText: string, linkUrl: string) => {
    pushClickEvent({ linkText, linkUrl });
};

const fallbackTermsLabel = () => (
    <>
        I accept the <a href={termsPath()} target="_blank" rel="noopener noreferrer" onClick={() => trackLegalLink('Terms and Conditions', termsPath())}>Terms and Conditions</a>,{' '}
        <a href={localePath('/collection-statement')} target="_blank" rel="noopener noreferrer" onClick={() => trackLegalLink('Collection Statement', localePath('/collection-statement'))}>Collection Statement</a> and{' '}
        <a href={localePath('/privacy')} target="_blank" rel="noopener noreferrer" onClick={() => trackLegalLink('Privacy Policy', localePath('/privacy'))}>Privacy Policy</a>.
    </>
);

const hasConsentCopy = (block?: BlockContent | null) => Array.isArray(block) && block.length > 0;

const consentLabel = (block: BlockContent | undefined, fallback: React.ReactNode) => (
    hasConsentCopy(block) ? renderParagraph(block) : fallback
);

const fieldFromSignup = (signup: SignupSection, name: string) => {
    const match = (signup.formFields || []).find(field => field.field === name);
    return {
        label: match && match.label ? match.label : name,
        error: match && match.error ? match.error : '',
        placeholder: PLACEHOLDERS[name],
    };
};

const consentsFromSignup = (signup: SignupSection): PledgeFormData['consents'] => [
    {
        id: PLEDGE_FIELD_IDS.marketingConsent,
        required: true,
        position: 'beforeButton',
        label: consentLabel(
            signup.optInConsentText,
            "I'm signing up as an awareness raiser and agree to receive Movember emails with tools, tips and updates.",
        ),
    },
    {
        id: PLEDGE_FIELD_IDS.termsPrivacy,
        required: true,
        position: 'beforeButton',
        label: consentLabel(signup.termsConsentText, fallbackTermsLabel()),
    },
];

export const buildPledgeFormFromSignup = (signup: SignupSection): PledgeFormData => ({
    firstNameField: fieldFromSignup(signup, 'firstName'),
    lastNameField: fieldFromSignup(signup, 'lastName'),
    emailField: fieldFromSignup(signup, 'email'),
    consents: consentsFromSignup(signup),
    btnText: signup.btnText || 'Sign up',
    formName: PLEDGE_FORM_NAME,
});

// Salesforce subscription shape from Moh's working test. Source is fixed.
const AWARENESS_SUBSCRIPTION_KEY = 'awareness';
const AWARENESS_SOURCE = 'contact';

// Always show these exact messages for empty required fields, even if the
// CMS field has no error copy configured.
const DEFAULT_FIRST_NAME_ERROR = 'First name is required';
const DEFAULT_LAST_NAME_ERROR = 'Last name is required';
const DEFAULT_EMAIL_ERROR = 'Invalid email address';
const DEFAULT_API_ERROR = 'Request failed';

// Java SimpleDateFormat yyyy-MM-dd'T'HH:mm:ss.SSSZ with a region offset, e.g. 2026-09-03T13:07:42.100+1000
const formatOptDatetime = (date: Date) => {
    const pad = (value: number, length = 2) => String(value).padStart(length, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absolute = Math.abs(offsetMinutes);

    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
        `.${pad(date.getMilliseconds(), 3)}` +
        `${sign}${pad(Math.floor(absolute / 60))}${pad(absolute % 60)}`
    );
};

const extractSfContactId = (record: { [key: string]: any } | undefined) => {
    if (!record || !record.sf_contact_id) return '';
    return String(record.sf_contact_id);
};

const PledgeForm: React.FC<Props> = ({
    cms,
    firstNameField,
    lastNameField,
    emailField,
    consents,
    btnText,
    formName,
    onSuccess,
    mockSubmit,
    hadPledged = false,
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [checked, setChecked] = useState<{ [id: string]: boolean }>({});
    const [isDirty, setIsDirty] = useState(false);
    const [requestFailed, setRequestFailed] = useState(false);
    // A ref, not state: two focuses inside one React batch would both read a
    // stale false and raise start_form twice.
    const hasStarted = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasCompleted, setHasCompleted] = useState(HIDE_FORM_AFTER_PLEDGE && hadPledged);
    const enteredFields = useRef<Set<string>>(new Set());
    const attemptNumber = useRef(0);

    const tracker = formName || PLEDGE_FORM_NAME;

    const isFirstNameValid = firstName.trim() !== '';
    const isLastNameValid = lastName.trim() !== '';
    const isEmailValid = email.trim() !== '' && isValidEmailAddress(email);
    const areConsentsValid = consents
        .filter(consent => consent.required)
        .every(consent => checked[consent.id]);

    const isFormValid = isFirstNameValid && isLastNameValid && isEmailValid && areConsentsValid;
    const marketingConsent = !!checked[PLEDGE_FIELD_IDS.marketingConsent];

    const onFormStart = () => {
        if (hasStarted.current) return;
        hasStarted.current = true;
        pushStartFormEvent({ formName: tracker });
    };

    const trackFieldEntered = (fieldId: string, hasValue: boolean) => {
        if (!hasValue || enteredFields.current.has(fieldId)) return;
        enteredFields.current.add(fieldId);
        pushEnterFieldFormEvent({ formName: tracker, fieldId });
    };

    const toggleConsent = (id: string, value: boolean) => {
        setChecked(prev => ({ ...prev, [id]: value }));
        trackFieldEntered(id, true);
    };

    const buildPayload = () => ({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        subscriptions: [
            {
                [AWARENESS_SUBSCRIPTION_KEY]: {
                    subscribed: true,
                    source: AWARENESS_SOURCE,
                    datetime: formatOptDatetime(new Date()),
                },
            },
        ],
        locale: getCurrentLocaleString('_'),
    });

    const pushValidationErrors = () => {
        if (!isFirstNameValid) {
            pushFormErrorFieldEvent({
                formName: tracker,
                fieldId: PLEDGE_FIELD_IDS.firstName,
                message: firstNameField.error || DEFAULT_FIRST_NAME_ERROR,
            });
        }
        if (!isLastNameValid) {
            pushFormErrorFieldEvent({
                formName: tracker,
                fieldId: PLEDGE_FIELD_IDS.lastName,
                message: lastNameField.error || DEFAULT_LAST_NAME_ERROR,
            });
        }
        if (!isEmailValid) {
            pushFormErrorFieldEvent({
                formName: tracker,
                fieldId: PLEDGE_FIELD_IDS.email,
                message: emailField.error || DEFAULT_EMAIL_ERROR,
            });
        }
    };

    const handleSuccess = (sfContactId?: string) => {
        setRequestFailed(false);
        setIsSubmitting(false);
        writeAwarenessPledge();
        // Super property on later events. Not identify() — distinct_id is unchanged.
        registerMixpanelSuperProperties({ awareness_raiser: true });
        pushFormCompleteEvent({
            formName: tracker,
            marketingConsent,
            attemptNumber: attemptNumber.current,
            pledgeStatus: hadPledged ? 'existing' : 'new',
            sfContactId: sfContactId || undefined,
        });
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (onSuccess) {
            onSuccess();
        }
        if (HIDE_FORM_AFTER_PLEDGE) {
            setHasCompleted(true);
            return;
        }
        // The form stays on the page, so clear it rather than leaving one
        // person's details in the fields for the next. attempt_number counts
        // retries within a single pledge, so it starts again for the next one.
        attemptNumber.current = 0;
        window.setTimeout(() => {
            setFirstName('');
            setLastName('');
            setEmail('');
            setChecked({});
            setIsDirty(false);
        }, 0);
    };

    const onSubmit = () => {
        if (isSubmitting || hasCompleted) return;
        setIsDirty(true);
        if (!isFormValid) {
            pushValidationErrors();
            return;
        }

        attemptNumber.current += 1;
        pushFormSubmitEvent({
            formName: tracker,
            marketingConsent,
            attemptNumber: attemptNumber.current,
        });

        if (mockSubmit) {
            handleSuccess();
            return;
        }

        setIsSubmitting(true);
        setRequestFailed(false);

        fetch(apiEndpoints.SF_data_subscription[getEnvironment()], {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildPayload()),
        })
            .then(res => res.json())
            .then(
                result => {
                    if (Array.isArray(result) && result[0] && result[0].success === true) {
                        handleSuccess(extractSfContactId(result[0]));
                        return;
                    }
                    setIsSubmitting(false);
                    setRequestFailed(true);
                    pushFormErrorFieldEvent({
                        formName: tracker,
                        message: DEFAULT_API_ERROR,
                    });
                },
                () => {
                    setIsSubmitting(false);
                    setRequestFailed(true);
                    pushFormErrorFieldEvent({
                        formName: tracker,
                        message: DEFAULT_API_ERROR,
                    });
                }
            );
    };

    const renderConsent = (consent: ConsentItem) => (
        <ConsentRow key={consent.id}>
            <ConsentCheckbox>
                <Input
                    id={consent.id}
                    name={consent.id}
                    type="checkbox"
                    checked={!!checked[consent.id]}
                    onChange={e => toggleConsent(consent.id, e.target.checked)}
                    hasError={isDirty && consent.required && !checked[consent.id]}
                    onFocusCapture={onFormStart}
                />
            </ConsentCheckbox>
            <ConsentLabel>{consent.label}</ConsentLabel>
        </ConsentRow>
    );

    const consentsBefore = consents.filter(consent => consent.position !== 'afterButton');
    const consentsAfter = consents.filter(consent => consent.position === 'afterButton');

    if (hasCompleted) {
        return null;
    }

    return (
        <FormWrapper>
            <NameRow>
                <Field>
                    <FieldLabel htmlFor={PLEDGE_FIELD_IDS.firstName}>{firstNameField.label}</FieldLabel>
                    <Input
                        id={PLEDGE_FIELD_IDS.firstName}
                        name={PLEDGE_FIELD_IDS.firstName}
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder={firstNameField.placeholder}
                        hasError={isDirty && !isFirstNameValid}
                        maxLength={50}
                        onFocusCapture={onFormStart}
                        onBlur={() => trackFieldEntered(PLEDGE_FIELD_IDS.firstName, firstName.trim() !== '')}
                    />
                    { isDirty && !isFirstNameValid &&
                        <FieldError>{firstNameField.error || DEFAULT_FIRST_NAME_ERROR}</FieldError>
                    }
                </Field>
                <Field>
                    <FieldLabel htmlFor={PLEDGE_FIELD_IDS.lastName}>{lastNameField.label}</FieldLabel>
                    <Input
                        id={PLEDGE_FIELD_IDS.lastName}
                        name={PLEDGE_FIELD_IDS.lastName}
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder={lastNameField.placeholder}
                        hasError={isDirty && !isLastNameValid}
                        maxLength={50}
                        onFocusCapture={onFormStart}
                        onBlur={() => trackFieldEntered(PLEDGE_FIELD_IDS.lastName, lastName.trim() !== '')}
                    />
                    { isDirty && !isLastNameValid &&
                        <FieldError>{lastNameField.error || DEFAULT_LAST_NAME_ERROR}</FieldError>
                    }
                </Field>
            </NameRow>

            <Field>
                <FieldLabel htmlFor={PLEDGE_FIELD_IDS.email}>{emailField.label}</FieldLabel>
                <Input
                    id={PLEDGE_FIELD_IDS.email}
                    name={PLEDGE_FIELD_IDS.email}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={emailField.placeholder}
                    hasError={isDirty && !isEmailValid}
                    maxLength={80}
                    onFocusCapture={onFormStart}
                    onBlur={() => trackFieldEntered(PLEDGE_FIELD_IDS.email, email.trim() !== '')}
                />
                { isDirty && !isEmailValid &&
                    <FieldError>{emailField.error || DEFAULT_EMAIL_ERROR}</FieldError>
                }
            </Field>

            {consentsBefore.map(renderConsent)}

            <ButtonRow>
                {/* Button stays grey until both boxes are ticked. Name and email are checked on click. */}
                <Button
                    btnType="primary"
                    btnText={btnText}
                    onClick={onSubmit}
                    onClickCapture={() => undefined}
                    isDisabled={!areConsentsValid || isSubmitting || hasCompleted}
                />
            </ButtonRow>

            {consentsAfter.map(renderConsent)}

            <ErrorText isShown={requestFailed}>
                {cms?.snippets?.['text-common-request-failed']?.text}
            </ErrorText>
        </FormWrapper>
    );
};

export default PledgeForm;
